import { ShipmentMethod as CrewReturnShipmentTypeEnum } from 'corso-types';
import Payments from '~/stores/payments';
import api from '~/utils/api';
import {
  isExchangeClaim,
  isGiftCardClaim,
  isRefundClaim,
  pendingClaimLineItemToCalculatedLineItem,
} from '~/utils/claim-line-item';
import { ClaimType, ExchangeMethod, ResolutionMethod } from '~/utils/constants';
import {
  formatDisplayValue,
  messagingInDays,
  toCurrency,
} from '~/utils/formatters';
import {
  makeCrewClaimWithClaimLineItemsLoader,
  redirect,
} from '~/utils/routing';
import { AsyncReturnType } from '~/utils/types';

type FormattedCurrency = ReturnType<
  ReturnType<typeof formatDisplayValue<number>>
>;

type ExchangeMethodOption = {
  type: ExchangeMethod;
  label: string;
  description: string;
};

type ClaimTotals = AsyncReturnType<typeof api.calculateBeforeClaimSubmission>;
type CrewContext = Parameters<
  Parameters<typeof makeCrewClaimWithClaimLineItemsLoader>[0]
>[0]['context'];

type SectionDataComputeParams = {
  settings: CrewContext['settings'];
  totals: ClaimTotals;
  items: CrewContext['crewClaim']['claimLineItems'];
  formatCurrency: (value: number) => FormattedCurrency;
};

export const toCustomerSectionData = (params: SectionDataComputeParams) => {
  const { totals, formatCurrency, items } = params;

  if (!totals?.toCustomer) return null;

  const { shipping, tax, subtotal } = totals.toCustomer;
  return {
    items,
    tax: formatCurrency(tax),
    subtotal: formatCurrency(subtotal),
    shipping: formatCurrency(shipping),
    total: formatCurrency(totals.toCustomer.total),
  };
};

export const fromCustomerSectionData = (params: SectionDataComputeParams) => {
  const {
    settings: {
      return: { isGiftCardEnabled },
    },
    totals: { fromCustomer },
    items,
    formatCurrency,
  } = params;

  // const { shipping, fee, subtotal, total, tax } = fromCustomer;

  const {
    adjustments: { shipping, fee, feeLabel },
    lineItems: { subtotal, tax },
    total,
  } = fromCustomer;
  // ? is this being computed on the back end?
  const incentive = isGiftCardEnabled ? fromCustomer.adjustments.incentive : 0;

  return {
    items,
    tax: formatCurrency(tax),
    subtotal: formatCurrency(subtotal),
    total: formatCurrency(total),
    incentive: formatCurrency(incentive),
    shipping: formatCurrency(shipping),
    fee: formatCurrency(fee),
    feeLabel,
  };
};

const getRefundMethod = (items: CrewContext['crewClaim']['claimLineItems']) => {
  if (items.some(isRefundClaim)) return ResolutionMethod.refund;
  if (items.some(isGiftCardClaim)) return ResolutionMethod.giftCard;

  return null;
};

const checkInstantExchangeAvailable = (context: CrewContext) => {
  const {
    settings,
    order,
    crewClaim: { claimLineItems, returnMethod },
  } = context;

  const stripeConfigured = Boolean(settings.stripeConfig);
  const instantExchangeOffered = Boolean(order?.offerInstantExchange);
  const allExchangeClaims = claimLineItems.every(isExchangeClaim);
  const hasReturnLabel =
    returnMethod?.type === CrewReturnShipmentTypeEnum.label;

  return (
    stripeConfigured &&
    instantExchangeOffered &&
    allExchangeClaims &&
    hasReturnLabel
  );
};

const getExchangeMethods = (
  offerInstantExchange: NonNullable<
    CrewContext['order']
  >['offerInstantExchange'],
) => {
  const timeFrame = offerInstantExchange?.timeToReturnBeforeCharging ?? 0;

  return [
    {
      type: ExchangeMethod.instant,
      label: 'Instant Exchange',
      description: `Use a credit card to get your items sent immediately, You will only be charged if you do not return your items within ${messagingInDays(
        timeFrame,
      )}.`,
    },
    {
      type: ExchangeMethod.standard,
      label: 'Exchange',
      description: 'Send your items back first, and get your new items later.',
    },
  ] satisfies ExchangeMethodOption[];
};

export const { loader, useLoaderData: useReviewLoaderData } =
  makeCrewClaimWithClaimLineItemsLoader(
    async ({ params: { store }, context }) => {
      const { crewClaim, order, settings } = context;
      if (!crewClaim.returnMethod) {
        return redirect(`/${store}`);
      }

      const toCustomerItems = crewClaim.claimLineItems.filter(isExchangeClaim);
      const fromCustomerItems = [...crewClaim.claimLineItems];

      const refundMethod = getRefundMethod(fromCustomerItems);
      const formatCurrency = formatDisplayValue(toCurrency(order.currencyCode));

      const claimTotals = await api.calculateBeforeClaimSubmission({
        params: { storefrontId: settings.storefrontId },
        body: {
          id: order.id,
          claimType: ClaimType.return,
          calculatedLineItems: crewClaim.claimLineItems.map((claim) =>
            pendingClaimLineItemToCalculatedLineItem(claim),
          ),
          returnShipment: crewClaim.returnMethod,
          toCustomerRate: crewClaim.toCustomerRate,
          //! Credit Resolution Methods is used to determine which line items to use when calculating the incentive, at this point we only want to explicitly pass it through if it's a gift card
          creditResolutionMethods:
            refundMethod === 'Gift_Card' ? [refundMethod] : [],
        },
      });

      if (claimTotals.returnSummary) return redirect('../return-summary');

      const fromCustomer = fromCustomerSectionData({
        settings,
        totals: claimTotals,
        items: fromCustomerItems,
        formatCurrency,
      });

      const toCustomer = toCustomerSectionData({
        settings,
        totals: claimTotals,
        items: toCustomerItems,
        formatCurrency,
      });

      const { balance } = claimTotals;
      const canInstantExchange = checkInstantExchangeAvailable(context);

      // the max amount that can be can be charge to the customer
      const authorizedAmount =
        canInstantExchange ? claimTotals.authorizedAmount : balance;

      const paymentIntentSecret =
        authorizedAmount > 0 && settings.stripeConfig ?
          await Payments.createPaymentIntent({
            storefrontId: settings.storefrontId,
            amount: authorizedAmount,
            currencyCode: order.currencyCode,
            stripeConfig: settings.stripeConfig,
            customerEmail: order.email,
          })
        : undefined;

      return {
        fromCustomer,
        toCustomer,
        refundMethod,
        balance: {
          value: balance,
          display: toCurrency(order.currencyCode)(Math.abs(balance)),
        },
        paymentIntentSecret,
        canInstantExchange,
        exchangeMethods: getExchangeMethods(order.offerInstantExchange),
        authorizedAmount: formatCurrency(authorizedAmount),
      };
    },
  );
